import React, { useMemo, useState } from 'react';
import {
  HoverCardContent,
  HoverCardRoot,
  HoverCardTrigger,
} from '@/components/ui/HoverCard';
import { Popover } from '@/components/ui';
import {
  Avatar,
  Badge,
  DataCell,
  DataList,
  Flex,
  ListItem,
  Separator,
  Text,
} from '@/components/ui';
import {
  IconCheck,
  IconX,
} from '@tabler/icons-react';
import type { FormField } from '@/types/studyForms';
import { getFieldIcon, getFieldTypeLabel } from '@/utils/formUtils';

interface FormFieldHoverCardProps {
  field: FormField;
  children: React.ReactNode;
  usePopover?: boolean; // Use Popover instead of HoverCard to avoid nested hover conflicts
  columnMapping?: {
    columnName: string;
    fileId?: string;
  }; // Optional column mapping information
}


// Field content component - shared between HoverCard and Popover
const FieldContent: React.FC<{
  field: FormField;
  columnMapping?: { columnName: string; fileId?: string };
}> = ({ field, columnMapping }) => {
  const FieldIcon = getFieldIcon(field.type);

  const fieldDetailsItems = useMemo(() => {
    const items: Array<
      { label: string; value: React.ReactNode } | { type: 'separator' }
    > = [
      { type: 'separator' },
      {
        label: 'Data Point',
        value: (
          <DataCell
            noWrapper
            type="text"
            value={field.dataPoint}
            size="small"
            render={() => (
              <Text
                size="3xsmall"
                weight="medium"
                style={{
                  fontFamily: 'var(--font-mono)',
                  wordBreak: 'break-word',
                }}
              >
                {field.dataPoint}
              </Text>
            )}
          />
        ),
      },
      {
        label: 'Field Type',
        value: (
          <DataCell
            noWrapper
            type="badge"
            value={getFieldTypeLabel(field.type)}
            variant="surface"
            color="info"
            size="small"
          />
        ),
      },
      {
        label: 'Required',
        value: (
          <DataCell
            noWrapper
            type="boolean"
            value={field.required}
            size="small"
            render={() => (
              <Flex align="center" gap="1">
                {field.required ? (
                  <Badge color="error" size="xsmall" startIcon={<IconCheck />}>
                    Yes
                  </Badge>
                ) : (
                  <Badge color="default" size="xsmall" startIcon={<IconX />}>
                    No
                  </Badge>
                )}
              </Flex>
            )}
          />
        ),
      },
    ];

    if (field.format) {
      items.push({
        label: 'Format',
        value: (
          <DataCell
            noWrapper
            type="text"
            value={field.format}
            size="small"
            render={() => (
              <Text
                size="3xsmall"
                style={{
                  fontFamily: 'var(--font-mono)',
                }}
              >
                {field.format}
              </Text>
            )}
          />
        ),
      });
    }

    return items;
  }, [field]);

  return (
    <Flex
      direction="column"
      gap="3"
      style={{ minWidth: '300px', maxWidth: '400px' }}
    >
      {/* Header */}
      <ListItem
        disableHoverEffect
        px="0"
        py="0"
        startIcon={
          <Avatar variant="ghost" radius="medium">
            <FieldIcon color="var(--blue-9)" />
          </Avatar>
        }
        primary={
          <Text size="2xsmall" weight="medium">
            {field.label}
          </Text>
        }
        secondary={
          <Flex align="center" gap="2" wrap="wrap">
            <Badge color="info" variant="surface" size="xsmall">
              {getFieldTypeLabel(field.type)}
            </Badge>
            {field.required && (
              <Badge color="error" variant="soft" size="small">
                Required
              </Badge>
            )}
          </Flex>
        }
      ></ListItem>

      {/* Field Details */}
      <DataList
        items={fieldDetailsItems}
        size="medium"
        orientation="horizontal"
        alignValues="end"
      />

      {/* Column Mapping Info */}
      {columnMapping && (
        <DataList
          items={[
            {
              label: 'CSV Column',
              value: (
                <DataCell
                  noWrapper
                  type="text"
                  value={columnMapping.columnName}
                  size="small"
                  render={() => (
                    <Text
                      size="2"
                      weight="medium"
                      style={{
                        fontFamily: 'var(--font-mono)',
                        wordBreak: 'break-word',
                      }}
                    >
                      {columnMapping.columnName}
                    </Text>
                  )}
                />
              ),
            },
          ]}
          size="medium"
          orientation="horizontal"
          alignValues="end"
        />
      )}

      {/* Allowed Values */}
      {field.allowedValues && field.allowedValues.length > 0 && (
        <>
          <Separator />
          <Flex direction="column" gap="2">
            <Text size="1" weight="medium" color="gray">
              Allowed Values ({field.allowedValues.length})
            </Text>
            <Flex
              direction="column"
              gap="1"
              style={{
                maxHeight: '150px',
                overflowY: 'auto',
                padding: '4px 0',
              }}
            >
              {field.allowedValues.slice(0, 20).map((value, index) => (
                <Flex
                  key={index}
                  align="center"
                  gap="2"
                  style={{
                    padding: '4px 8px',
                    borderRadius: '4px',
                    background: 'var(--gray-1)',
                  }}
                >
                  <Text size="1" style={{ fontFamily: 'var(--font-mono)' }}>
                    {value}
                  </Text>
                </Flex>
              ))}
              {field.allowedValues.length > 20 && (
                <Text size="1" color="gray" style={{ padding: '4px 8px' }}>
                  +{field.allowedValues.length - 20} more values
                </Text>
              )}
            </Flex>
          </Flex>
        </>
      )}
    </Flex>
  );
};

export const FormFieldHoverCard: React.FC<FormFieldHoverCardProps> = ({
  field,
  children,
  usePopover = false,
  columnMapping,
}) => {
  const [popoverOpen, setPopoverOpen] = useState(false);

  // Use Popover for nested contexts (like inside modals or other HoverCards)
  // Popover works on click, avoiding conflicts with nested HoverCards
  if (usePopover) {
    return (
      <Popover.Root open={popoverOpen} onOpenChange={setPopoverOpen}>
        <Popover.Trigger
          style={{
            border: 'none',
            background: 'transparent',
            padding: 0,
            cursor: 'pointer',
            display: 'inline-block',
            width: '100%',
          }}
        >
          {children}
        </Popover.Trigger>
        <Popover.Content
          size="medium"
          maxWidth="400px"
          side="right"
          align="start"
        >
          <FieldContent field={field} columnMapping={columnMapping} />
        </Popover.Content>
      </Popover.Root>
    );
  }

  // Use HoverCard for normal contexts
  return (
    <HoverCardRoot>
      {/* @ts-expect-error - asChild is supported by Radix UI but not in types */}
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent size="2">
        <FieldContent field={field} columnMapping={columnMapping} />
      </HoverCardContent>
    </HoverCardRoot>
  );
};
